import type { User } from '@h2h/shared';
import { useNavigate } from 'react-router-dom';
import { Avatar, Skeleton } from '../../components/ui/bits';
import { LeagueSwitcher } from '../../components/ui/LeagueSwitcher';
import {
  Ban,
  Check,
  ChevronLeft,
  MoreHorizontal,
  Pencil,
  Share2,
  Swords,
  UserPlus
} from '../../components/ui/icons';
import { tierName } from '@h2h/shared';
import { cap } from '../../lib/format';
import { shareWithFeedback } from '../../lib/share';
import { joinedLabel } from '../profile/helpers';
import type { PlayerProfileVM } from './usePlayerProfileScreen';

export function PlayerProfileHero({
  vm,
  name,
  tier,
  loading
}: {
  vm: PlayerProfileVM;
  name: string;
  tier: number;
  loading: boolean;
}) {
  const navigate = useNavigate();
  const {
    isSelf,
    isPublicRoute,
    isBlocked,
    isFollowing,
    leagueId,
    setLeagueId,
    sharedMemberships,
    targetUser,
    targetUserId,
    me,
    addFriend,
    removeFriend,
    unblockUser,
    setMenuOpen
  } = vm;

  const firstName = name.split(/\s+/)[0];
  const profileUser = (isSelf ? me : targetUser) as User | null | undefined;

  return (
    <div className="relative mt-4 overflow-hidden rounded-[28px] bg-gradient-to-br from-[#1C1C24] to-[#0A0A0E] p-5 shadow-[0_22px_42px_-18px_rgba(0,0,0,0.55)]">
      <span className="pointer-events-none absolute -right-[40px] -top-[50px] h-[200px] w-[200px] rounded-full bg-[radial-gradient(circle,rgba(252,222,124,0.16),transparent_70%)]" />
      <span className="pointer-events-none absolute -left-[60px] bottom-[-60px] h-[200px] w-[200px] rounded-full bg-[radial-gradient(circle,rgba(36,197,106,0.14),transparent_72%)]" />
      <div className="relative">
        {isPublicRoute && (
          <div className="mb-4 flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="tap -ml-1 flex items-center gap-[2px] text-white/90"
            >
              <ChevronLeft width={22} height={22} strokeWidth={2.4} />
              <span className="text-[15px] font-semibold">Back</span>
            </button>
            {!isSelf && (
              <button
                type="button"
                aria-label="More options"
                onClick={() => setMenuOpen(true)}
                className="tap grid h-[34px] w-[34px] place-items-center rounded-full bg-white/10 text-white"
              >
                <MoreHorizontal width={18} height={18} strokeWidth={2.2} />
              </button>
            )}
          </div>
        )}

        <div className="flex items-center gap-4">
          <Avatar
            name={name}
            tone={profileUser?.avatarTone ?? 0}
            size={64}
            src={profileUser?.photoURL}
            className="ring-2 ring-white/12"
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[21px] font-extrabold tracking-tight text-white">{name}</div>
            <div className="mt-[2px] truncate text-[13px] font-medium text-white/55">
              {profileUser?.username ? `@${profileUser.username}` : isSelf ? vm.user?.email : 'Player'}
              {profileUser?.createdAt ? ` · joined ${joinedLabel(profileUser.createdAt)}` : ''}
            </div>
          </div>
        </div>

        {leagueId && (
          <div className="mt-5 border-t border-white/[0.08] pt-4">
            <div className="mb-[9px] text-[10px] font-bold uppercase tracking-eyebrow text-white/40">
              {isSelf ? 'Viewing your stats in' : `Viewing ${firstName} in`}
            </div>
            <LeagueSwitcher
              leagueId={leagueId}
              tab={isSelf && !isPublicRoute ? 'profile' : null}
              size="sm"
              variant="dark"
              memberships={isSelf ? undefined : sharedMemberships}
              onSelectLeague={isPublicRoute ? setLeagueId : undefined}
              subtitle={
                loading ? (
                  <Skeleton tone="dark" className="h-3 w-14 translate-y-[1px]" />
                ) : (
                  <span className="animate-fade-in">{cap(tierName(tier))}</span>
                )
              }
            />
          </div>
        )}

        {!isSelf && isBlocked && (
          <div className="mt-[18px] flex items-center gap-[11px] rounded-[16px] border border-white/15 bg-white/[0.06] px-[14px] py-3">
            <Ban width={17} height={17} strokeWidth={2.2} className="shrink-0 text-white/55" />
            <span className="flex-1 text-[13px] font-semibold text-white/75">You blocked this player.</span>
            <button
              type="button"
              disabled={unblockUser.isPending}
              onClick={() => vm.onUnblock()}
              className="tap rounded-[10px] bg-white px-[14px] py-[7px] text-[13px] font-bold text-ink"
            >
              Unblock
            </button>
          </div>
        )}

        <div className="mt-[18px] flex gap-[10px]">
          {isSelf ? (
            <>
              <button
                type="button"
                onClick={() => navigate('/settings/profile')}
                className="tap flex flex-1 items-center justify-center gap-[7px] rounded-[14px] bg-white py-[11px] text-[14px] font-extrabold text-ink"
              >
                <Pencil width={15} height={15} strokeWidth={2.2} />
                Edit profile
              </button>
              <button
                type="button"
                onClick={() =>
                  void shareWithFeedback(
                    {
                      title: `${name} on HeadToHead`,
                      text: `Add ${name} on HeadToHead — challenge them in your league`,
                      url: profileUser?.userId
                        ? `${window.location.origin}/u/${profileUser.userId}${leagueId ? `?league=${leagueId}` : ''}`
                        : window.location.origin
                    },
                    'Profile link copied'
                  )
                }
                className="tap flex items-center justify-center gap-[7px] rounded-[14px] border border-white/[0.16] bg-white/10 px-[18px] py-[11px] text-[14px] font-bold text-white"
              >
                <Share2 width={16} height={16} strokeWidth={2} />
                Share
              </button>
            </>
          ) : (
            !isBlocked && (
              <>
                <button
                  type="button"
                  onClick={() => navigate(`/challenge/${targetUserId}`, { state: { leagueId } })}
                  className="tap flex flex-1 items-center justify-center gap-[8px] rounded-[14px] bg-accent py-[11px] text-[14px] font-extrabold text-white shadow-[0_10px_20px_-10px_rgba(10,160,110,0.65)]"
                >
                  <Swords width={15} height={15} strokeWidth={2} />
                  Challenge
                </button>
                <button
                  type="button"
                  disabled={addFriend.isPending || removeFriend.isPending}
                  onClick={() => (isFollowing ? removeFriend.mutate(targetUserId) : addFriend.mutate({ userId: targetUserId }))}
                  className="tap flex items-center justify-center gap-[6px] rounded-[14px] border border-white/[0.16] bg-white/10 px-[14px] py-[11px] text-[14px] font-bold text-white"
                >
                  {isFollowing ? (
                    <>
                      <Check width={15} height={15} strokeWidth={2.6} /> Friends
                    </>
                  ) : (
                    <>
                      <UserPlus width={15} height={15} strokeWidth={2.4} /> Add
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    void shareWithFeedback(
                      {
                        title: `${name} on HeadToHead`,
                        text: `Check out ${name} on HeadToHead`,
                        url: `${window.location.origin}/u/${targetUserId}${leagueId ? `?league=${leagueId}` : ''}`
                      },
                      'Profile link copied'
                    )
                  }
                  className="tap grid w-[46px] place-items-center rounded-[14px] border border-white/[0.16] bg-white/10 text-white"
                >
                  <Share2 width={16} height={16} strokeWidth={2} />
                </button>
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
}
